import React, { useCallback, useEffect, useState } from 'react';
import './SendMailScreen.css';
import { useSuccessBar } from '../context/SuccessBarContext';
import { usePopup } from '../context/PopupContext';
import ActionButton from '../input/ActionButton';
import Textfield from '../input/Textfield';
import CustomDropdown from '../input/CustomDropdown';
import api from '../wrapper/API';
import Popup from '../popup/Popup';
import Icon from '../home/Icon';
import Tooltip from '../tooltip/Tooltip';
import { isSelectableInEventMode, isSelectableInPlayerMode } from '../../util/textblockScope';
import { findTextblockVariant } from '../../util/textblockVariants';
import TextblockVariantHint from './TextblockVariantHint';

import { useEditor, BubbleMenu, EditorContent } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import BulletList from '@tiptap/extension-bullet-list';
import ListItem from '@tiptap/extension-list-item';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import History from '@tiptap/extension-history';
import { Color } from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import HardBreak from '@tiptap/extension-hard-break';

function SendMailScreen({ postEvent, postPlayer, onMailSent }) {
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();
  const { openPopup } = usePopup();

  // Kein postEvent übergeben → Direktversand an Spieler (ohne Spieltag-Kontext).
  const isPlayerMode = !postEvent;

  const [selectedTextblockId, setSelectedTextblockId] = useState(null);
  const [subject, setSubject] = useState('');
  const [error, setError] = useState('');
  const [textblocks, setTextblocks] = useState([]);

  const availableTextblocks = textblocks.filter((tb) =>
    isPlayerMode ? isSelectableInPlayerMode(tb.sendScope) : isSelectableInEventMode(tb.sendScope)
  );

  // Location variant of the chosen block for the event's location (GetAllNames carries the variants).
  // The preview (GET /Textblock/GetByName) is rendered by the server, which picks the same variant; without
  // an event (player mode) the default always applies.
  const usedVariant = !isPlayerMode && selectedTextblockId
    ? findTextblockVariant(textblocks.find((tb) => tb.id === selectedTextblockId), postEvent?.locationId)
    : null;

  const editor = useEditor({
    extensions: [
      Document, Paragraph, Text, BulletList, ListItem, Bold, Italic,
      Link.configure({ openOnClick: false, autolink: true, defaultProtocol: 'https' }),
      Underline, History, Color, TextStyle, HardBreak,
    ],
    content: '',
  });

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  useEffect(() => {
    api.get('/Textblock/GetAllNames?language=DE')
      .then((response) => { setTextblocks(response.data); })
      .catch(() => {});
  }, []);

  if (!editor) return null;

  const handleDropdownChange = (event) => {
    const selectedId = event.target.value;
    if (!selectedId) {
      setSelectedTextblockId(null);
      setSubject('');
      editor.commands.setContent('');
      return;
    }
    if (!postPlayer?.id) return;
    if (!isPlayerMode && !postEvent?.id) return;

    setSelectedTextblockId(parseInt(selectedId));

    const previewUrl = isPlayerMode
      ? `/Textblock/GetPreviewForPlayer?textblockId=${selectedId}&playerId=${postPlayer.id}`
      : `/Textblock/GetByName?textblockId=${selectedId}&eventId=${postEvent.id}&playerId=${postPlayer.id}`;

    api.get(previewUrl)
      .then((response) => {
        editor.commands.setContent(response.data.body ?? '');
        setSubject(response.data.subject ?? '');
      })
      .catch((err) => {
        setError(err.response?.data ?? 'Vorschau konnte nicht geladen werden.');
        openPopup('mailrerror', 1);
      });
  };

  const sendMail = () => {
    if (!subject.trim()) {
      setError('Bitte gib einen Betreff ein.');
      openPopup('mailrerror', 1);
      return;
    }
    startSuccessBarLoading('E-Mail wird in Versand-Queue gestellt');

    const request = isPlayerMode
      ? api.post('/Textblock/SendPlayerMail', {
          textblockId: selectedTextblockId ?? null,
          playerId: postPlayer.id,
          subject,
          body: editor.getHTML(),
        })
      : api.post('/Textblock/SendCustomMail', {
          textblockId: selectedTextblockId ?? null,
          eventId: postEvent.id,
          playerId: postPlayer.id,
          subject,
          body: editor.getHTML(),
        });

    request
      .then(() => {
        startSuccessBarSuccess('E-Mail wird im Hintergrund versendet');
        setSelectedTextblockId(null);
        setSubject('');
        editor.commands.setContent('');
        onMailSent?.();
      })
      .catch((err) => {
        closeSuccessBar();
        setError(err.response?.data ?? 'E-Mail konnte nicht gesendet werden.');
        openPopup('mailrerror', 1);
      });
  };

  return (
    <React.Fragment>
      <div className={'innerContainer'}>
      <div className={'generalInformationContainer'}>
        <label className={'innerContainerLabel'}>Empfänger</label>
        {!isPlayerMode && (
          <Textfield icon={'event'} type={'date'} name={'date'} placeholder={'Datum'} readOnly value={postEvent?.date ?? ''} />
        )}
        <Textfield icon={'id_card'} type={'text'} name={'nachname'} placeholder={'Nachname'} readOnly value={postPlayer?.lastName ?? ''} />
        <Textfield icon={'person'} type={'text'} name={'vorname'} placeholder={'Vorname'} readOnly value={postPlayer?.firstName ?? ''} />
        <Textfield icon={'send'} type={'text'} name={'email'} placeholder={'E-Mail'} readOnly value={postPlayer?.email ?? ''} />

        <label className={'innerContainerLabel'}>Textbaustein</label>
        <CustomDropdown value={selectedTextblockId ? String(selectedTextblockId) : ''} onChange={handleDropdownChange} icon={'message'}>
          <option value=''>– Kein Textbaustein –</option>
          {availableTextblocks.map((tb) => (
            <option key={tb.id} value={tb.id}>{tb.frontendName}</option>
          ))}
        </CustomDropdown>
        <TextblockVariantHint variant={usedVariant} />

        <label className={'innerContainerLabel'}>Betreff</label>
        <Textfield icon={'subject'} type={'text'} name={'betreff'} placeholder={'Betreff eingeben ...'} value={subject} onChange={(e) => setSubject(e.target.value)} />

        <div className={'buttonsIconMailContainer'}>
          <ActionButton icon="send" label="Senden" type={2} onClick={sendMail} />
        </div>
      </div>

      <div className={'sideContainer'}>
        <label className={'innerContainerLabel'}>E-Mail bearbeiten</label>
        <BubbleMenu editor={editor}>
          <div className={'bubbleMenu'}>
            <Tooltip text='Rückgängig'>
              <button type='button' onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
                <Icon className={'bubbleIcon'} icon={'undo'} />
              </button>
            </Tooltip>
            <Tooltip text='Wiederholen'>
              <button type='button' onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
                <Icon className={'bubbleIcon'} icon={'redo'} />
              </button>
            </Tooltip>
            <Tooltip text='Fett'>
              <button type='button' onClick={() => editor.chain().focus().toggleBold().run()} className={editor.isActive('bold') ? 'isActive' : ''}>
                <Icon className={'bubbleIcon'} icon={'format_bold'} />
              </button>
            </Tooltip>
            <Tooltip text='Kursiv'>
              <button type='button' onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive('italic') ? 'isActive' : ''}>
                <Icon className={'bubbleIcon'} icon={'format_italic'} />
              </button>
            </Tooltip>
            <Tooltip text='Link'>
              <button type='button' onClick={setLink} className={editor.isActive('link') ? 'isActive' : ''}>
                <Icon className={'bubbleIcon'} icon={'link'} />
              </button>
            </Tooltip>
            <Tooltip text='Liste'>
              <button type='button' onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor.isActive('bulletList') ? 'isActive' : ''}>
                <Icon className={'bubbleIcon'} icon={'format_list_bulleted'} />
              </button>
            </Tooltip>
            <Tooltip text='Farbe'>
              <input
                type='color'
                onInput={(e) => editor.chain().focus().setColor(e.target.value).run()}
                value={editor.getAttributes('textStyle').color || '#000000'}
              />
            </Tooltip>
            <Tooltip text='Farbe zurücksetzen'>
              <button type='button' onClick={() => editor.chain().focus().unsetColor().run()}>
                <Icon className={'bubbleIcon'} icon={'format_color_reset'} />
              </button>
            </Tooltip>
          </div>
        </BubbleMenu>
        <EditorContent editor={editor} />
      </div>
      </div>

      <Popup name='mailrerror' confirmText='Ok' headerText='Error' type='error'>
        <p>{error}</p>
      </Popup>
    </React.Fragment>
  );
}

export default SendMailScreen;
